import { API_URL } from "../../../actions/settings";
import { TrackingColumnDef } from "./trackingColumns";

// A sheet row: user-defined cells plus building/customer fields derived on the server.
export type TrackingRowData = Record<string, any> & {
  _rowId: string;
  building_id?: string;
  building?: string;
  customer_id?: string;
  customer?: string;
};

const authHeader = () => {
  const tkn = localStorage.getItem("token") ?? "";
  return `Token ${tkn.slice(1, -1)}`;
};

async function request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}/tracking/${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status !== "success") {
    throw new Error(json.message || json.detail || "Request failed.");
  }
  return json.data as T;
}

// A saved layout is AG Grid column state (order is the array order)
export type ColumnLayout = { colId: string; [key: string]: any }[];

// A saved view: layout + sort, column filters and search text, private to the user
export interface TrackingView {
  id: number;
  name: string;
  column_state: ColumnLayout;
  filter_model: Record<string, any>;
  search: string;
}
export type ViewSnapshot = Omit<TrackingView, "id" | "name">;

export const trackingApi = {
  getSheet: () =>
    request<{ columns: TrackingColumnDef[]; rows: TrackingRowData[]; can_edit: boolean; layout: ColumnLayout | null; views: TrackingView[] }>(""),
  saveLayout: (column_state: ColumnLayout) => request<ColumnLayout>("layout", "PUT", { column_state }),
  createView: (name: string, snapshot: ViewSnapshot) => request<TrackingView>("views", "POST", { name, ...snapshot }),
  updateView: (id: number, snapshot: ViewSnapshot) => request<TrackingView>(`views/${id}`, "PUT", snapshot),
  deleteView: (id: number) => request<null>(`views/${id}`, "DELETE"),
  resetLayout: () => request<null>("layout", "DELETE"),
  addColumn: (col: { label: string; type: string; options?: string[] }) =>
    request<TrackingColumnDef>("columns", "POST", col),
  deleteColumn: (id: string) => request<null>(`columns/${id}`, "DELETE"),
  resetColumns: () => request<{ columns: TrackingColumnDef[] }>("columns/reset", "POST"),
  updateRow: (id: string, changes: { data: Record<string, any> }) =>
    request<TrackingRowData>(`rows/${id}`, "PATCH", changes),
};
